/**
 * Augments Horizon's price-facet with a dual-handle range slider.
 *
 * It finds each <price-facet-component>, reads min=0/max=range_max, injects a
 * slider, and syncs values bidirectionally with the existing text inputs so
 * Horizon's form-submission logic keeps working.
 *
 * Robustness: the host <price-range-slider> carries data-skip-(node|subtree)-update,
 * so Horizon's morph leaves the JS-built DOM intact across facet re-renders (the
 * server renders an empty element — without the skip flags a morph would wipe the
 * track/handles and the slider would vanish after the first interaction). Because
 * the element is preserved, connectedCallback runs once; a MutationObserver on the
 * text inputs re-syncs the handles when the price filter is cleared or changed
 * elsewhere (morph updates input values without firing input events).
 */

const THUMB_SIZE = 20;

class PriceRangeSlider extends HTMLElement {
  connectedCallback() {
    // Self-guard: only build once. Survives morph (which preserves this element).
    if (this.dataset.ready === 'true' || this.querySelector('.price-range-slider__track')) return;

    const facet = this.closest('price-facet-component');
    if (!facet) return;

    // Resolve the two text inputs unambiguously. Both fields carry a `data-max`
    // attribute, so a `[data-max]` selector would match the min field first —
    // prefer the explicit refs, fall back to DOM order.
    const priceInputs = facet.querySelectorAll('input.price-facet__input');
    const minInput = facet.querySelector('input[ref="minInput"]') || priceInputs[0];
    const maxInput = facet.querySelector('input[ref="maxInput"]') || priceInputs[1];
    if (!minInput || !maxInput || minInput === maxInput) return;

    const max = parseInt(maxInput.dataset.max, 10);
    if (!Number.isFinite(max) || max <= 0) return;

    this.maxRange = max;
    this.symbol = this.dataset.currencySymbol || '€';
    this.maxDisplay = this.dataset.rangeMaxDisplay || String(max);
    this.minTextInput = minInput;
    this.maxTextInput = maxInput;

    const minValue = this.parseValue(minInput.value, 0);
    const maxValue = this.parseValue(maxInput.value, max);

    this.innerHTML = `
      <div class="price-range-slider__bubble price-range-slider__bubble--min" aria-hidden="true"></div>
      <div class="price-range-slider__bubble price-range-slider__bubble--max" aria-hidden="true"></div>
      <div class="price-range-slider__track" aria-hidden="true">
        <div class="price-range-slider__fill"></div>
      </div>
      <input type="range" class="price-range-slider__input price-range-slider__input--min"
             min="0" max="${max}" value="${minValue}" step="1" aria-label="Minimumprijs">
      <input type="range" class="price-range-slider__input price-range-slider__input--max"
             min="0" max="${max}" value="${maxValue}" step="1" aria-label="Maximumprijs">
      <div class="price-range-slider__bounds" aria-hidden="true">
        <span class="price-range-slider__bound">${this.symbol} 0</span>
        <span class="price-range-slider__bound">${this.symbol} ${this.maxDisplay}</span>
      </div>
    `;

    this.minRange = this.querySelector('.price-range-slider__input--min');
    this.maxRangeEl = this.querySelector('.price-range-slider__input--max');
    this.fill = this.querySelector('.price-range-slider__fill');
    this.bubbleMin = this.querySelector('.price-range-slider__bubble--min');
    this.bubbleMax = this.querySelector('.price-range-slider__bubble--max');

    this.handleSliderInput = this.handleSliderInput.bind(this);
    this.handleSliderChange = this.handleSliderChange.bind(this);
    this.handleTextInput = this.handleTextInput.bind(this);

    this.minRange.addEventListener('input', this.handleSliderInput);
    this.maxRangeEl.addEventListener('input', this.handleSliderInput);
    this.minRange.addEventListener('change', this.handleSliderChange);
    this.maxRangeEl.addEventListener('change', this.handleSliderChange);
    minInput.addEventListener('input', this.handleTextInput);
    maxInput.addEventListener('input', this.handleTextInput);

    // Re-sync when a morph rewrites the text inputs (e.g. price filter cleared).
    // Morph sets value via setAttribute without dispatching input events.
    this.textObserver = new MutationObserver(() => this.handleTextInput());
    this.textObserver.observe(minInput, { attributes: true, attributeFilter: ['value'] });
    this.textObserver.observe(maxInput, { attributes: true, attributeFilter: ['value'] });

    this.updateUI();
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.textObserver?.disconnect();
  }

  parseValue(raw, fallback) {
    if (raw === undefined || raw === null || raw === '') return fallback;
    const cleaned = String(raw).replace(/[^0-9.,]/g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? Math.round(num) : fallback;
  }

  /** Human-readable value for a handle bubble (€ + integer euros). */
  format(value) {
    if (value <= 0) return `${this.symbol} 0`;
    if (value >= this.maxRange) return `${this.symbol} ${this.maxDisplay}`;
    return `${this.symbol} ${value}`;
  }

  /** Left offset of a bubble, centred over the native thumb (which is inset by its radius). */
  handlePosition(value) {
    const pct = this.maxRange > 0 ? value / this.maxRange : 0;
    return `calc(${pct * 100}% + ${(0.5 - pct) * THUMB_SIZE}px)`;
  }

  handleSliderInput(event) {
    let lo = parseInt(this.minRange.value, 10);
    let hi = parseInt(this.maxRangeEl.value, 10);
    if (lo > hi) {
      if (event.target === this.minRange) lo = hi;
      else hi = lo;
      this.minRange.value = String(lo);
      this.maxRangeEl.value = String(hi);
    }
    this.updateUI();
  }

  handleSliderChange() {
    const lo = parseInt(this.minRange.value, 10);
    const hi = parseInt(this.maxRangeEl.value, 10);

    const newMinText = lo > 0 ? String(lo) : '';
    const newMaxText = hi < this.maxRange ? String(hi) : '';

    if (this.minTextInput.value !== newMinText) {
      this.minTextInput.value = newMinText;
      this.dispatchInputEvent(this.minTextInput);
    }
    if (this.maxTextInput.value !== newMaxText) {
      this.maxTextInput.value = newMaxText;
      this.dispatchInputEvent(this.maxTextInput);
    }
  }

  handleTextInput() {
    const lo = this.parseValue(this.minTextInput.value, 0);
    const hi = this.parseValue(this.maxTextInput.value, this.maxRange);
    const clampedLo = Math.max(0, Math.min(lo, this.maxRange));
    const clampedHi = Math.max(0, Math.min(hi, this.maxRange));
    if (parseInt(this.minRange.value, 10) !== clampedLo) {
      this.minRange.value = String(clampedLo);
    }
    if (parseInt(this.maxRangeEl.value, 10) !== clampedHi) {
      this.maxRangeEl.value = String(clampedHi);
    }
    this.updateUI();
  }

  updateUI() {
    if (this.maxRange <= 0) return;
    const lo = parseInt(this.minRange.value, 10) || 0;
    const hi = parseInt(this.maxRangeEl.value, 10) || this.maxRange;

    this.style.setProperty('--fill-start', `${(lo / this.maxRange) * 100}%`);
    this.style.setProperty('--fill-end', `${100 - (hi / this.maxRange) * 100}%`);

    this.bubbleMin.textContent = this.format(lo);
    this.bubbleMax.textContent = this.format(hi);
    this.bubbleMin.style.left = this.handlePosition(lo);
    this.bubbleMax.style.left = this.handlePosition(hi);
  }

  dispatchInputEvent(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}
